import cx from 'classnames';
import { useState } from 'react';
import { Button, ButtonVariants } from '@components/button/Button';
import { ExternalRefundLink } from '@components/externalRefundLink/ExternalRefundLink';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { centsToDollar } from '@shared/utils/currency';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { collectCancellationFee } from 'restaurantAdmin/reservations/apiHelpers';
import typography from '~styles/typography.scss';
import styles from './CancelConfirmationModal.scss';

export interface CollectCancellationFeeConfirmationModalProps {
  cancellationPolicy: {
    fee: number;
  } | null;
  closeModal: () => void;
  disablePortal?: boolean;
  handleAfterConfirm?: () => void;
  isOpen: boolean;
  reservationId: string;
}

export const CollectCancellationFeeConfirmationModal = ({
  cancellationPolicy,
  closeModal,
  disablePortal,
  handleAfterConfirm,
  isOpen,
  reservationId,
}: CollectCancellationFeeConfirmationModalProps) => {
  const restaurant = useRestaurant();

  const [isPending, setIsPending] = useState(false);

  const handleCollectFee = () => {
    setIsPending(true);

    void (async () => {
      try {
        await collectCancellationFee(restaurant.id, reservationId);
        successToast({
          message:
            "Success: The guest's reservation cancellation fee has been collected.",
        });
        handleAfterConfirm?.();
        closeModal();
      } catch (error) {
        if (error instanceof ApiError) {
          errorToast({ message: error.message });
        } else {
          errorToast({
            message:
              'Failed to collect cancellation fee. Please try again or contact support.',
          });
        }
        reportAppError(error);
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <Modal
      ariaLabel="Collect cancellation fee confirmation"
      disablePortal={disablePortal}
      isOpen={isOpen}
      onClose={closeModal}
    >
      <h1 className={typography.d3}>Collect cancellation fee</h1>
      <p className={cx(typography.c2, styles.message)}>
        Click confirm to charge the guest for the{' '}
        {centsToDollar(cancellationPolicy?.fee ?? 0)} cancellation fee.
      </p>
      <p className={cx(typography.c2, styles.message)}>
        Please reference the Peak <ExternalRefundLink className={styles.link} />{' '}
        for more detail regarding cancellations.
      </p>
      <ModalActions>
        <Button
          label="Go Back"
          onClick={closeModal}
          isDisabled={isPending}
          variant={ButtonVariants.Tertiary}
        />
        <Button
          isDisabled={isPending}
          label="Confirm"
          onClick={handleCollectFee}
          variant={ButtonVariants.Primary}
        />
      </ModalActions>
    </Modal>
  );
};
